/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { NavIcon, useBrandTokens } from '../../../shell/shared';
import { usePowerSyncDb, usePowerSyncQuery } from '../../../adapters/powersync';
import {
  qFormDefinitionById,
  qFormTemplatePages,
  qFormTemplateMappings,
  parseFieldsSchemaV1,
  stringifyFieldsSchemaV1,
  flattenFields,
  type FormDefinitionRow,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  type FormFieldDef,
  type FormFieldsSchemaV1,
  type FormSection,
  type BindingConfig,
  type FormFieldBindingMode,
  type FormFieldBindingSource,
  type DomainGroup,
  type GeneratedFieldConfig,
  getBindingSourceOptions,
  getSampleDataMap,
  getSystemFieldByKey,
  isBindingModeAllowed,
  // PRD.305 follow-up — Lab Work purpose + Laboratory Services assignment
  qAppointmentServices,
  qLabWorkFormAssignments,
  qNoOp,
  selectableLabFormServices,
  laboratoryServiceFormId,
  formPurposeToStored,
  formPurposeFromStored,
  FORM_PURPOSES,
  FORM_PURPOSE_LABELS,
  type FormPurpose,
  parseRichText,
  type RichTextConfig,
  readMappingPresentation,
  writeMappingPresentation,
  supportsMappingAlignment,
  supportsMappingLabel,
  resolveMappingLabelText,
  MAPPING_ALIGNMENTS,
  MAPPING_LABEL_POSITIONS,
  type MappingPresentation,
  FORM_HEADER_FIELD_KEY,
  FORM_HEADER_FIELD_TYPE,
  FORM_HEADER_DEFAULTS,
  FORM_HEADER_COLORS,
  FORM_HEADER_COLOR_LABELS,
  FORM_HEADER_COLOR_VALUES,
  FORM_HEADER_SIZES,
  isFormHeaderMapping,
  readFormHeaderConfig,
  writeFormHeaderConfig,
  type FormHeaderConfig,
} from '@helios/data-access';
import { useAuth } from '@helios/auth-firebase-web';
import { uploadFormTemplateImage } from '../../../lib/upload-form-template';
import { RichTextEditor, RichTextView } from '../../../components/RichTextField';
import { MappedFieldBox } from '../../../components/MappedFieldBox';
import { FormHeaderBox } from '../../../components/FormHeaderBox';
import { TemplateOverlayView } from '../../patient/encounters/FormFieldRenderers';

/** Narrow a field's `config` to the Rich Text shape. Never returns undefined. */
function richTextConfig(field: FormFieldDef): RichTextConfig {
  return (field.config as RichTextConfig | undefined) ?? {};
}

// ─── Constants ───

const STEPS = [
  { num: 1, label: 'Define Fields' },
  { num: 2, label: 'Upload Images' },
  { num: 3, label: 'Map Fields' },
  { num: 4, label: 'Preview' },
  { num: 5, label: 'Save Draft' },
  { num: 6, label: 'Publish' },
] as const;

const FIELD_TYPES = [
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'multi-select',
  'yes-no',
  'binding',
  'drawing',
  'signature',
  'rich-text',
] as const;

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  textarea: 'Text Area',
  number: 'Number',
  date: 'Date',
  select: 'Select',
  'multi-select': 'Multi-Select',
  'yes-no': 'Yes / No',
  binding: 'Binding',
  drawing: 'Drawing',
  signature: 'Signature',
  'rich-text': 'Rich Text',
};

/** Derived from the system field registry — single source of truth. */
const BINDING_SOURCES = getBindingSourceOptions();

/** Derived from the system field registry — single source of truth. */
const SAMPLE_DATA: Record<string, string> = getSampleDataMap();

const STANDARD_TYPES = new Set([
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'multi-select',
  'yes-no',
]);

/** Widget types eligible for binding metadata (V2) */
const BINDABLE_TYPES = new Set([
  'text',
  'textarea',
  'number',
  'date',
  'select',
  'multi-select',
  'yes-no',
]);

const BINDING_MODE_LABELS: Record<FormFieldBindingMode, string> = {
  read_only: 'Read-only (display only)',
  default_only: 'Prefill only (editable, no write-back)',
  two_way: 'Editable binding (writes back)',
};

const DOMAIN_GROUP_OPTIONS: { value: DomainGroup; label: string }[] = [
  { value: 'demographics', label: 'Demographics' },
  { value: 'contact', label: 'Contact Details' },
  { value: 'identifiers', label: 'Identifiers' },
  { value: 'emergency_contact', label: 'Emergency Contact' },
  { value: 'medication', label: 'Medication' },
  { value: 'allergies', label: 'Allergies' },
  { value: 'notes_alerts', label: 'Notes & Alerts' },
  { value: 'encounter', label: 'Encounter' },
];

// ─── ID + key helpers ───

let _uidC = 0;
function uid(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${(++_uidC).toString(36)}`;
}

function toFieldKey(label: string): string {
  return (
    label
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '') || `field_${Date.now()}`
  );
}

// ─── Validation errors ───

interface FormValidationError {
  id: string;
  message: string;
  sectionId?: string;
  fieldKey?: string;
}

function validateSchema(schema: FormFieldsSchemaV1): FormValidationError[] {
  const errors: FormValidationError[] = [];
  const keysSeen = new Set<string>();
  for (const section of schema.sections) {
    if (!section.title.trim()) {
      errors.push({
        id: `s-${section.id}`,
        message: `Section "${section.id}" has no title`,
        sectionId: section.id,
      });
    }
    for (const f of section.fields) {
      if (!f.label.trim()) {
        errors.push({
          id: `f-${f.key}-label`,
          message: `Field "${f.key}" has no label`,
          sectionId: section.id,
          fieldKey: f.key,
        });
      }
      if (!f.key.trim()) {
        errors.push({
          id: `f-${f.key}-key`,
          message: `A field in "${section.title || 'Untitled'}" has no key`,
          sectionId: section.id,
          fieldKey: f.key,
        });
      } else if (keysSeen.has(f.key)) {
        errors.push({
          id: `f-${f.key}-dup`,
          message: `Duplicate field key "${f.key}"`,
          sectionId: section.id,
          fieldKey: f.key,
        });
      }
      keysSeen.add(f.key);
      if (
        (f.type === 'select' || f.type === 'multi-select') &&
        (!f.options || f.options.length === 0)
      ) {
        errors.push({
          id: `f-${f.key}-opts`,
          message: `"${f.label || f.key}" (${f.type}) has no options`,
          sectionId: section.id,
          fieldKey: f.key,
        });
      }
    }
  }
  return errors;
}

// ─── Selection model ───

type Selected =
  | { type: 'section'; sectionId: string }
  | { type: 'field'; sectionId: string; fieldKey: string }
  | null;

// ─── Step Progress Bar ───

function StepProgress({
  currentStep,
  onStepClick,
  primary,
}: {
  currentStep: number;
  onStepClick: (s: number) => void;
  primary: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '0 var(--sp-5)',
        marginBottom: 'var(--sp-4)',
      }}
    >
      {STEPS.map((step, i) => {
        const isActive = step.num === currentStep;
        const isCompleted = step.num < currentStep;
        return (
          <div key={step.num} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button
              onClick={() => onStepClick(step.num)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 'var(--radius-full)',
                border: isActive ? `2px solid ${primary}` : '1px solid var(--border)',
                background: isCompleted ? primary : isActive ? `${primary}14` : 'var(--bg-surface)',
                color: isCompleted ? '#fff' : isActive ? primary : 'var(--text-muted)',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 'var(--font-weight-medium)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: '50%',
                  background: isCompleted ? '#fff' : isActive ? primary : 'var(--border)',
                  color: isCompleted ? primary : isActive ? '#fff' : 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {isCompleted ? '\u2713' : step.num}
              </span>
              {step.label}
            </button>
            {i < STEPS.length - 1 && (
              <div
                style={{
                  width: 20,
                  height: 1,
                  background: isCompleted ? primary : 'var(--border)',
                }}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Toggle Switch ───

function ToggleSwitch({ active, onToggle }: { active: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        border: 'none',
        background: active ? 'var(--color-primary, #2563eb)' : 'var(--bg-surface-2, #e5e7eb)',
        cursor: 'pointer',
        position: 'relative',
        transition: 'background 150ms',
        padding: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: active ? 22 : 2,
          width: 20,
          height: 20,
          borderRadius: 'var(--radius-full)',
          background: '#fff',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          transition: 'left 150ms',
        }}
      />
    </button>
  );
}

// ─── Drag Handle ───

function GripIcon() {
  return (
    <svg
      width="10"
      height="16"
      viewBox="0 0 10 16"
      fill="currentColor"
      style={{ display: 'block' }}
    >
      <circle cx="2.5" cy="2" r="1.5" />
      <circle cx="7.5" cy="2" r="1.5" />
      <circle cx="2.5" cy="8" r="1.5" />
      <circle cx="7.5" cy="8" r="1.5" />
      <circle cx="2.5" cy="14" r="1.5" />
      <circle cx="7.5" cy="14" r="1.5" />
    </svg>
  );
}

// ─── Step 1: Define Fields (Section-based builder) ───

function StepDefineFields({
  schema,
  onChange,
}: {
  schema: FormFieldsSchemaV1;
  onChange: (s: FormFieldsSchemaV1) => void;
}) {
  const [selected, setSelected] = useState<Selected>(null);
  const dragSectionRef = useRef<number | null>(null);
  const [dropSectionIdx, setDropSectionIdx] = useState<number | null>(null);
  const dragFieldRef = useRef<{ sectionId: string; index: number } | null>(null);
  const [dropFieldTarget, setDropFieldTarget] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);

  const errors = useMemo(() => validateSchema(schema), [schema]);
  const sectionErrorSet = useMemo(
    () => new Set(errors.filter((e) => e.sectionId).map((e) => e.sectionId!)),
    [errors],
  );
  const fieldErrorSet = useMemo(
    () => new Set(errors.filter((e) => e.fieldKey).map((e) => e.fieldKey!)),
    [errors],
  );

  // ── Helpers ──
  const updateSchema = (sections: FormSection[]) => onChange({ ...schema, sections });

  const addSection = () => {
    const s: FormSection = { id: uid('s'), title: '', fields: [] };
    updateSchema([...schema.sections, s]);
    setSelected({ type: 'section', sectionId: s.id });
  };

  const removeSection = (sectionId: string) => {
    updateSchema(schema.sections.filter((s) => s.id !== sectionId));
    if (selected?.sectionId === sectionId) setSelected(null);
  };

  const updateSection = (sectionId: string, patch: Partial<FormSection>) => {
    updateSchema(schema.sections.map((s) => (s.id === sectionId ? { ...s, ...patch } : s)));
  };

  const addField = (sectionId: string) => {
    const f: FormFieldDef = {
      key: uid('f'),
      label: '',
      type: 'text',
      required: false,
      layout: 'full',
    };
    updateSchema(
      schema.sections.map((s) => (s.id === sectionId ? { ...s, fields: [...s.fields, f] } : s)),
    );
    setSelected({ type: 'field', sectionId, fieldKey: f.key });
  };

  const removeField = (sectionId: string, fieldKey: string) => {
    updateSchema(
      schema.sections.map((s) =>
        s.id === sectionId ? { ...s, fields: s.fields.filter((f) => f.key !== fieldKey) } : s,
      ),
    );
    if (selected?.type === 'field' && selected.fieldKey === fieldKey) setSelected(null);
  };

  const updateField = (sectionId: string, fieldKey: string, patch: Partial<FormFieldDef>) => {
    updateSchema(
      schema.sections.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              fields: s.fields.map((f) => (f.key === fieldKey ? { ...f, ...patch } : f)),
            }
          : s,
      ),
    );
    // If the key itself changed, update selection to track the new key
    if (
      patch.key &&
      patch.key !== fieldKey &&
      selected?.type === 'field' &&
      selected.fieldKey === fieldKey
    ) {
      setSelected({ type: 'field', sectionId, fieldKey: patch.key });
    }
  };

  // ── Drag: sections ──
  const onSectionDragStart = (idx: number, e: React.DragEvent) => {
    dragSectionRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
  };
  const onSectionDragOver = (idx: number, e: React.DragEvent) => {
    e.preventDefault();
    if (dragSectionRef.current !== null && dragSectionRef.current !== idx) setDropSectionIdx(idx);
  };
  const onSectionDrop = (idx: number) => {
    const from = dragSectionRef.current;
    if (from !== null && from !== idx) {
      const arr = [...schema.sections];
      const [moved] = arr.splice(from, 1);
      arr.splice(idx, 0, moved);
      updateSchema(arr);
    }
    dragSectionRef.current = null;
    setDropSectionIdx(null);
  };
  const onSectionDragEnd = () => {
    dragSectionRef.current = null;
    setDropSectionIdx(null);
  };

  // ── Drag: fields ──
  const onFieldDragStart = (sectionId: string, index: number, e: React.DragEvent) => {
    dragFieldRef.current = { sectionId, index };
    e.dataTransfer.effectAllowed = 'move';
  };
  const onFieldDragOver = (sectionId: string, index: number, e: React.DragEvent) => {
    e.preventDefault();
    const src = dragFieldRef.current;
    if (src && src.sectionId === sectionId && src.index !== index) {
      setDropFieldTarget({ sectionId, index });
    }
  };
  const onFieldDrop = (sectionId: string, index: number) => {
    const src = dragFieldRef.current;
    if (src && src.sectionId === sectionId && src.index !== index) {
      const section = schema.sections.find((s) => s.id === sectionId);
      if (section) {
        const arr = [...section.fields];
        const [moved] = arr.splice(src.index, 1);
        arr.splice(index, 0, moved);
        updateSection(sectionId, { fields: arr });
      }
    }
    dragFieldRef.current = null;
    setDropFieldTarget(null);
  };
  const onFieldDragEnd = () => {
    dragFieldRef.current = null;
    setDropFieldTarget(null);
  };

  // ── Selected item data ──
  const selectedSection = selected
    ? schema.sections.find((s) => s.id === selected.sectionId)
    : null;
  const selectedField =
    selected?.type === 'field' && selectedSection
      ? selectedSection.fields.find((f) => f.key === selected.fieldKey)
      : null;

  return (
    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Left: Builder */}
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto', padding: 'var(--sp-5)' }}>
        {/* Validation banner */}
        {errors.length > 0 && (
          <div
            style={{
              background: 'var(--color-danger-bg, #fef2f2)',
              border: '1px solid var(--color-danger-border, #fecaca)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-4)',
              marginBottom: 'var(--sp-4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-2)',
            }}
          >
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                color: 'var(--color-danger-text, #dc2626)',
              }}
            >
              {errors.length} validation {errors.length === 1 ? 'error' : 'errors'}
            </span>
            <ul
              style={{
                margin: 0,
                paddingLeft: 'var(--sp-5)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
              }}
            >
              {errors.map((err) => (
                <li
                  key={err.id}
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-danger-text, #dc2626)',
                  }}
                >
                  {err.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Section cards */}
        {schema.sections.map((section, sIdx) => {
          const isSectionSelected =
            selected?.type === 'section' && selected.sectionId === section.id;
          return (
            <div
              key={section.id}
              draggable
              onDragStart={(e) => onSectionDragStart(sIdx, e)}
              onDragOver={(e) => onSectionDragOver(sIdx, e)}
              onDrop={() => onSectionDrop(sIdx)}
              onDragEnd={onSectionDragEnd}
              style={{
                background: 'var(--bg-surface-2, #f9fafb)',
                border: isSectionSelected
                  ? '1px solid var(--color-primary)'
                  : '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                overflow: 'hidden',
                marginBottom: 'var(--sp-3)',
                borderTop: dropSectionIdx === sIdx ? '2px solid var(--color-primary)' : undefined,
              }}
            >
              {/* Section header */}
              <div
                onClick={() => setSelected({ type: 'section', sectionId: section.id })}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-4) var(--sp-5)',
                  cursor: 'pointer',
                  borderBottom: '1px solid var(--border)',
                }}
              >
                <span style={{ color: 'var(--text-placeholder)', cursor: 'grab', flexShrink: 0 }}>
                  <GripIcon />
                </span>
                <span
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 'var(--radius-full)',
                    background: 'var(--color-primary)',
                    color: '#fff',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 'var(--font-weight-semi)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {sIdx + 1}
                </span>
                <span
                  style={{
                    flex: 1,
                    fontSize: 'var(--font-size-base)',
                    fontWeight: 'var(--font-weight-semi)',
                    color: 'var(--text)',
                  }}
                >
                  {section.title || '(untitled section)'}
                </span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                  {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
                </span>
                {sectionErrorSet.has(section.id) && (
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 'var(--radius-full)',
                      background: 'var(--color-danger-text, #dc2626)',
                      flexShrink: 0,
                    }}
                  />
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSection(section.id);
                  }}
                  style={iconBtnStyle}
                  title="Remove section"
                >
                  <NavIcon name="x" />
                </button>
              </div>

              {/* Field rows */}
              <div
                style={{
                  padding: 'var(--sp-3) var(--sp-5)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--sp-1)',
                }}
              >
                {section.fields.map((field, fIdx) => {
                  const isFieldSelected =
                    selected?.type === 'field' && selected.fieldKey === field.key;
                  const isDragOver =
                    dropFieldTarget?.sectionId === section.id && dropFieldTarget.index === fIdx;
                  return (
                    <div
                      key={field.key}
                      draggable
                      onClick={() =>
                        setSelected({ type: 'field', sectionId: section.id, fieldKey: field.key })
                      }
                      onDragStart={(e) => onFieldDragStart(section.id, fIdx, e)}
                      onDragOver={(e) => onFieldDragOver(section.id, fIdx, e)}
                      onDrop={() => onFieldDrop(section.id, fIdx)}
                      onDragEnd={onFieldDragEnd}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-md)',
                        background: isFieldSelected ? 'rgba(37,99,235,0.08)' : 'transparent',
                        border: isFieldSelected
                          ? '1px solid var(--color-primary)'
                          : '1px solid transparent',
                        cursor: 'pointer',
                        borderTop: isDragOver ? '2px solid var(--color-primary)' : undefined,
                      }}
                    >
                      <span
                        style={{ color: 'var(--text-placeholder)', cursor: 'grab', flexShrink: 0 }}
                      >
                        <GripIcon />
                      </span>
                      <FieldTypeBadge type={field.type} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 'var(--font-size-base)',
                            fontWeight: 'var(--font-weight-medium)',
                            color: 'var(--text)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {field.label || '(untitled)'}
                        </div>
                        <span
                          style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--text-placeholder)',
                            fontFamily: 'monospace',
                          }}
                        >
                          {field.key || '—'}
                        </span>
                        {/* PRD.322 — formatted but BOUNDED preview. Editing
                            belongs in Field Properties, so this is display
                            only and is height-capped to keep long consent
                            wording from dominating the canvas. */}
                        {field.type === 'rich-text' && (
                          <div
                            style={{
                              marginTop: 6,
                              padding: '6px 8px',
                              maxHeight: 120,
                              overflow: 'hidden',
                              borderLeft: '3px solid var(--border)',
                              background: 'var(--bg-surface-2, #f8fafc)',
                              borderRadius: 'var(--radius-sm)',
                              color: 'var(--text-muted)',
                            }}
                          >
                            <RichTextView
                              doc={parseRichText(richTextConfig(field).content ?? '')}
                              style={{ fontSize: 'var(--font-size-xs)' }}
                            />
                          </div>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--sp-2)',
                          flexShrink: 0,
                        }}
                      >
                        {field.layout === 'full' ? (
                          <span style={badgeStyle}>Full</span>
                        ) : (
                          <span style={badgeStyle}>½</span>
                        )}
                        {field.required && (
                          <span
                            style={{
                              ...badgeStyle,
                              background: 'rgba(37,99,235,0.12)',
                              color: 'var(--color-primary)',
                            }}
                          >
                            Required
                          </span>
                        )}
                        {fieldErrorSet.has(field.key) && (
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: 'var(--radius-full)',
                              background: 'var(--color-danger-text, #dc2626)',
                              flexShrink: 0,
                            }}
                          />
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeField(section.id, field.key);
                        }}
                        style={iconBtnStyle}
                      >
                        <NavIcon name="x" />
                      </button>
                    </div>
                  );
                })}
                <button
                  onClick={() => addField(section.id)}
                  style={{ ...addButtonStyle, marginTop: 'var(--sp-1)' }}
                >
                  <NavIcon name="plus" /> Add Field
                </button>
              </div>
            </div>
          );
        })}

        <button
          onClick={addSection}
          style={{
            ...addButtonStyle,
            width: '100%',
            justifyContent: 'center',
            marginTop: 'var(--sp-2)',
          }}
        >
          <NavIcon name="plus" /> Add Section
        </button>
      </div>

      {/* Right: Properties Panel
          PRD.322 — widened from a fixed 320px so a Rich Text editor and its
          toolbar are usable. Percentage-based with desktop bounds, and
          `flexShrink: 0` is dropped so the panel can still give way on a
          narrow window instead of squeezing the canvas to nothing. */}
      <div
        style={{
          width: '30%',
          minWidth: 420,
          maxWidth: 540,
          flexShrink: 1,
          borderLeft: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{ padding: 'var(--sp-4) var(--sp-5)', borderBottom: '1px solid var(--border)' }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            {selected?.type === 'section'
              ? 'Section Properties'
              : selected?.type === 'field'
                ? 'Field Properties'
                : 'Properties'}
          </span>
        </div>
        <div
          style={{
            padding: 'var(--sp-5)',
            overflowY: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
          }}
        >
          {!selected && (
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
              Select a section or field to edit its properties.
            </p>
          )}

          {/* Section properties */}
          {selected?.type === 'section' && selectedSection && (
            <>
              <div>
                <label style={labelStyle}>Title</label>
                <input
                  type="text"
                  value={selectedSection.title}
                  onChange={(e) => updateSection(selectedSection.id, { title: e.target.value })}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Description</label>
                <textarea
                  value={selectedSection.description ?? ''}
                  onChange={(e) =>
                    updateSection(selectedSection.id, { description: e.target.value || undefined })
                  }
                  rows={3}
                  style={{
                    ...inputStyle,
                    height: 'auto',
                    minHeight: 64,
                    padding: '10px',
                    resize: 'vertical',
                  }}
                />
              </div>
            </>
          )}

          {/* Field properties */}
          {selected?.type === 'field' && selectedSection && selectedField && (
            <FieldPropertiesPanel
              field={selectedField}
              onUpdate={(patch) => updateField(selectedSection.id, selectedField.key, patch)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * PRD.324 — Rich Text IS mappable, reversing PRD.322's V1 exclusion.
 *
 * PRD.322 excluded it because a mapping was a fixed rectangle and rich content
 * could only have been flattened into it. PRD.324 removes that premise: a Rich
 * Text mapping is a FLOW ANCHOR whose height comes from the content, so the
 * formatting survives and the box is a start position rather than a clip.
 *
 * Every field type is mappable again, so this predicate is now the identity —
 * kept as a named constant because the rule has changed once and the call sites
 * document why they filter.
 */
const RENDER_MAPPABLE = (_f: FormFieldDef) => true;

// ─── Field Properties Panel ───

function FieldPropertiesPanel({
  field,
  onUpdate,
}: {
  field: FormFieldDef;
  onUpdate: (patch: Partial<FormFieldDef>) => void;
}) {
  const isStandard = STANDARD_TYPES.has(field.type);
  const showOptions = field.type === 'select' || field.type === 'multi-select';

  return (
    <>
      {/* Label */}
      <div>
        <label style={labelStyle}>Label</label>
        <input
          type="text"
          value={field.label}
          onChange={(e) => {
            const label = e.target.value;
            const patch: Partial<FormFieldDef> = { label };
            const currentKeyIsAuto =
              field.key.startsWith('f-') ||
              field.key.startsWith('field_') ||
              field.key === toFieldKey(field.label);
            if (currentKeyIsAuto) patch.key = toFieldKey(label);
            onUpdate(patch);
          }}
          style={inputStyle}
        />
      </div>

      {/* Field Key */}
      <div>
        <label style={labelStyle}>Field Key</label>
        <input
          type="text"
          value={field.key}
          onChange={(e) => onUpdate({ key: e.target.value.replace(/[^a-z0-9_]/g, '') })}
          placeholder="snake_case"
          style={{ ...inputStyle, fontFamily: 'monospace' }}
        />
      </div>

      {/* Type */}
      <div>
        <label style={labelStyle}>Type</label>
        <select
          value={field.type}
          onChange={(e) => {
            const type = e.target.value as FormFieldDef['type'];
            const patch: Partial<FormFieldDef> = { type };
            if (type === 'binding') {
              // Legacy path — still supported for existing schemas
              patch.config = { source: 'patient', path: 'full_name' } as BindingConfig;
              patch.binding = undefined; // Legacy mode uses config, not V2 binding
            } else if (type === 'drawing') {
              patch.config = { placeholder: 'Drawing Area' };
              patch.binding = undefined;
            } else {
              patch.config = undefined;
            }
            if (type !== 'select' && type !== 'multi-select') patch.options = undefined;
            if (!STANDARD_TYPES.has(type)) patch.validation = undefined;
            if (type === 'signature') {
              patch.layout = 'full';
              patch.binding = undefined;
            }
            onUpdate(patch);
          }}
          style={inputStyle}
        >
          {FIELD_TYPES.map((t) => (
            <option key={t} value={t}>
              {FIELD_TYPE_LABELS[t] ?? t}
            </option>
          ))}
        </select>
      </div>

      {/* PRD.322 — Rich Text content lives in the field's own `config`, so it
          needs no schema change and PRD.321 clones it with `fields_schema`. */}
      {field.type === 'rich-text' && (
        <>
          <div>
            <label style={labelStyle}>Content</label>
            <RichTextEditor
              ariaLabel="Rich text content"
              value={richTextConfig(field).content ?? ''}
              onChange={(content) => onUpdate({ config: { ...richTextConfig(field), content } })}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <label style={labelStyle}>Editable when filling</label>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  lineHeight: 1.5,
                }}
              >
                {richTextConfig(field).editableWhenFilling
                  ? 'Copied to each form once, then edited per patient.'
                  : 'Shown as fixed wording. Not an answer field.'}
              </div>
            </div>
            <ToggleSwitch
              active={!!richTextConfig(field).editableWhenFilling}
              onToggle={() =>
                onUpdate({
                  config: {
                    ...richTextConfig(field),
                    editableWhenFilling: !richTextConfig(field).editableWhenFilling,
                  },
                  // Read-only wording is not an answer, so a stale `required`
                  // must not survive the toggle back.
                  required: richTextConfig(field).editableWhenFilling ? false : field.required,
                })
              }
            />
          </div>
        </>
      )}

      {/* Required */}
      {(isStandard ||
        field.type === 'signature' ||
        // Read-only Rich Text has no required semantics at all (PRD §Validation).
        (field.type === 'rich-text' && richTextConfig(field).editableWhenFilling)) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={labelStyle}>Required</label>
          <ToggleSwitch
            active={!!field.required}
            onToggle={() => onUpdate({ required: !field.required })}
          />
        </div>
      )}

      {/* Help Text */}
      {isStandard && (
        <div>
          <label style={labelStyle}>Help Text</label>
          <input
            type="text"
            value={field.helpText ?? ''}
            onChange={(e) => onUpdate({ helpText: e.target.value || undefined })}
            placeholder="Optional"
            style={inputStyle}
          />
        </div>
      )}

      {/* Width (signature is always full) */}
      {isStandard && (
        <div>
          <label style={labelStyle}>Width</label>
          <div style={{ display: 'flex', gap: 0 }}>
            {(['half', 'full'] as const).map((val) => {
              const active = (field.layout ?? 'full') === val;
              return (
                <button
                  key={val}
                  onClick={() => onUpdate({ layout: val })}
                  style={{
                    flex: 1,
                    height: 36,
                    border: '1px solid var(--border)',
                    background: active ? 'var(--color-primary)' : 'var(--bg-surface)',
                    color: active ? '#fff' : 'var(--text)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    cursor: 'pointer',
                    borderRadius:
                      val === 'half'
                        ? 'var(--radius-md) 0 0 var(--radius-md)'
                        : '0 var(--radius-md) var(--radius-md) 0',
                    marginLeft: val === 'full' ? -1 : 0,
                  }}
                >
                  {val === 'half' ? 'Half' : 'Full'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Options for select/multi-select */}
      {showOptions && (
        <div>
          <label style={labelStyle}>Options</label>
          {(field.options ?? []).map((opt) => (
            <div
              key={opt.id}
              style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}
            >
              <input
                type="text"
                value={opt.label}
                onChange={(e) => {
                  const opts = (field.options ?? []).map((o) =>
                    o.id === opt.id ? { ...o, label: e.target.value } : o,
                  );
                  onUpdate({ options: opts });
                }}
                placeholder="Label"
                style={{ ...inputStyle, flex: 1 }}
              />
              <input
                type="text"
                value={opt.value}
                onChange={(e) => {
                  const opts = (field.options ?? []).map((o) =>
                    o.id === opt.id ? { ...o, value: e.target.value } : o,
                  );
                  onUpdate({ options: opts });
                }}
                placeholder="Value"
                style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
              />
              <button
                onClick={() =>
                  onUpdate({ options: (field.options ?? []).filter((o) => o.id !== opt.id) })
                }
                style={iconBtnStyle}
              >
                <NavIcon name="x" />
              </button>
            </div>
          ))}
          <button
            onClick={() =>
              onUpdate({
                options: [...(field.options ?? []), { id: uid('opt'), label: '', value: '' }],
              })
            }
            style={addButtonStyle}
          >
            <NavIcon name="plus" /> Add Option
          </button>
        </div>
      )}

      {/* Legacy binding config (type: 'binding') */}
      {field.type === 'binding' && (
        <div>
          <label style={labelStyle}>Data Source (Legacy)</label>
          <select
            value={
              field.config
                ? `${(field.config as BindingConfig).source}.${(field.config as BindingConfig).path}`
                : ''
            }
            onChange={(e) => {
              const [source, ...pathParts] = e.target.value.split('.');
              onUpdate({ config: { source, path: pathParts.join('.') } as BindingConfig });
            }}
            style={inputStyle}
          >
            <option value="">Select source...</option>
            {BINDING_SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* V2 Binding metadata — available on any bindable widget type */}
      {BINDABLE_TYPES.has(field.type) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={labelStyle}>Bind to Data Source</label>
            <ToggleSwitch
              active={!!field.binding}
              onToggle={() => {
                if (field.binding) {
                  onUpdate({ binding: undefined });
                } else {
                  // Default to first registry entry
                  const first = BINDING_SOURCES[0];
                  const entry = first ? getSystemFieldByKey(first.value) : undefined;
                  const patch: Partial<FormFieldDef> = {
                    binding: {
                      mode: 'read_only' as FormFieldBindingMode,
                      source: (entry?.source ?? 'patient') as FormFieldBindingSource,
                      bindingKey: entry
                        ? entry.bindingKey.split('.').slice(1).join('.')
                        : 'full_name',
                      domainGroup: entry?.domainGroup,
                    },
                  };
                  // Auto-seed top-level domainGroup if not already set
                  if (!field.domainGroup && entry?.domainGroup) {
                    patch.domainGroup = entry.domainGroup;
                  }
                  onUpdate(patch);
                }
              }}
            />
          </div>
          {field.binding &&
            (() => {
              const currentFullKey = `${field.binding.source}.${field.binding.bindingKey}`;
              return (
                <>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 'var(--font-size-xs)' }}>
                      Bound Field
                    </label>
                    <select
                      value={currentFullKey}
                      onChange={(e) => {
                        const selectedKey = e.target.value;
                        const entry = getSystemFieldByKey(selectedKey);
                        const [source, ...pathParts] = selectedKey.split('.');
                        // Auto-set domainGroup from registry; reset mode if not allowed
                        const currentMode = field.binding!.mode;
                        const mode = isBindingModeAllowed(selectedKey, currentMode)
                          ? currentMode
                          : 'read_only';
                        const patch: Partial<FormFieldDef> = {
                          binding: {
                            ...field.binding!,
                            source: source as FormFieldBindingSource,
                            bindingKey: pathParts.join('.'),
                            domainGroup: entry?.domainGroup,
                            mode,
                          },
                        };
                        // Auto-seed top-level domainGroup from registry if not explicitly set
                        if (!field.domainGroup && entry?.domainGroup) {
                          patch.domainGroup = entry.domainGroup;
                        }
                        onUpdate(patch);
                      }}
                      style={inputStyle}
                    >
                      <option value="">Select field...</option>
                      {BINDING_SOURCES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize: 'var(--font-size-xs)' }}>
                      Binding Mode
                    </label>
                    <select
                      value={field.binding.mode}
                      onChange={(e) =>
                        onUpdate({
                          binding: {
                            ...field.binding!,
                            mode: e.target.value as FormFieldBindingMode,
                          },
                        })
                      }
                      style={inputStyle}
                    >
                      {(
                        Object.entries(BINDING_MODE_LABELS) as [FormFieldBindingMode, string][]
                      ).map(([mode, label]) => (
                        <option
                          key={mode}
                          value={mode}
                          disabled={!isBindingModeAllowed(currentFullKey, mode)}
                        >
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              );
            })()}
        </div>
      )}

      {/* Drawing info */}
      {field.type === 'drawing' && (
        <div
          style={{
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-info-bg, #eff6ff)',
            border: '1px solid var(--color-info, #3b82f6)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-info, #1d4ed8)',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          Drawing region — freehand ink area. Position and size it in Step 3.
        </div>
      )}

      {/* Signature info */}
      {field.type === 'signature' && (
        <div
          style={{
            padding: 'var(--sp-3)',
            borderRadius: 'var(--radius-sm)',
            background: '#fef2f2',
            border: '1px solid #dc2626',
            fontSize: 'var(--font-size-xs)',
            color: '#991b1b',
            lineHeight: 'var(--line-height-sm)',
          }}
        >
          Signature field — position the signature box in Step 3.
        </div>
      )}

      {/* Domain Group */}
      <div>
        <label style={labelStyle}>Domain Group</label>
        <select
          value={field.domainGroup ?? ''}
          onChange={(e) => {
            const val = e.target.value;
            onUpdate({ domainGroup: val ? (val as DomainGroup) : undefined });
          }}
          style={inputStyle}
        >
          <option value="">No Group</option>
          {DOMAIN_GROUP_OPTIONS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>

      {/* Auto-Generate Value */}
      {isStandard && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={labelStyle}>Auto-Generate Value</label>
            <ToggleSwitch
              active={!!field.generated}
              onToggle={() => {
                if (field.generated) {
                  onUpdate({ generated: undefined });
                } else {
                  onUpdate({
                    generated: {
                      strategy: 'auto_increment' as const,
                      pattern: '{auto_increment}',
                    } satisfies GeneratedFieldConfig,
                  });
                }
              }}
            />
          </div>
          {field.generated && (
            <>
              <div>
                <label style={{ ...labelStyle, fontSize: 'var(--font-size-xs)' }}>Pattern</label>
                <input
                  type="text"
                  value={field.generated.pattern}
                  onChange={(e) =>
                    onUpdate({
                      generated: { ...field.generated!, pattern: e.target.value },
                    })
                  }
                  placeholder="{auto_increment}"
                  style={inputStyle}
                />
                <p
                  style={{
                    margin: 'var(--sp-1) 0 0',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    lineHeight: 'var(--line-height-sm)',
                  }}
                >
                  Placeholders: {'{auto_increment}'}, {'{year}'}, {'{month}'}, {'{day}'}
                  <br />
                  Examples: {'{auto_increment}'}, P-{'{auto_increment}'}, {'{year}'}-
                  {'{auto_increment}'}
                </p>
              </div>
              <div>
                <label style={{ ...labelStyle, fontSize: 'var(--font-size-xs)' }}>
                  Pad Width (zero-fill)
                </label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={field.generated.padWidth ?? ''}
                  onChange={(e) => {
                    const val = e.target.value ? parseInt(e.target.value, 10) : undefined;
                    onUpdate({
                      generated: { ...field.generated!, padWidth: val || undefined },
                    });
                  }}
                  placeholder="None"
                  style={inputStyle}
                />
                <p
                  style={{
                    margin: 'var(--sp-1) 0 0',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                  }}
                >
                  {field.generated.padWidth
                    ? `e.g. 42 → ${String(42).padStart(field.generated.padWidth, '0')}`
                    : 'No zero-padding'}
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {/* Validation controls for standard types */}
      {isStandard && <FieldValidationControls field={field} onUpdate={onUpdate} />}
    </>
  );
}

// ─── Field Validation Controls ───

function FieldValidationControls({
  field,
  onUpdate,
}: {
  field: FormFieldDef;
  onUpdate: (patch: Partial<FormFieldDef>) => void;
}) {
  const rules = (field.validation ?? []) as Array<{ kind: string; value?: unknown }>;
  const findRule = (kind: string) => rules.find((r) => r.kind === kind);
  const setRule = (kind: string, rule: { kind: string; value?: unknown } | null) => {
    const next = rules.filter((r) => r.kind !== kind);
    if (rule) next.push(rule);
    onUpdate({ validation: next });
  };

  if (field.type === 'text' || field.type === 'textarea') {
    const formatRule = findRule('string.format');
    const minRule = findRule('string.minLength');
    const maxRule = findRule('string.maxLength');
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          borderTop: '1px solid var(--border)',
          paddingTop: 'var(--sp-4)',
        }}
      >
        <label style={{ ...labelStyle, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Validation
        </label>
        <div>
          <label style={labelStyle}>Format</label>
          <select
            value={(formatRule?.value as string) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRule('string.format', v ? { kind: 'string.format', value: v } : null);
            }}
            style={inputStyle}
          >
            <option value="">None</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="alpha">Alpha</option>
            <option value="alphanumeric">Alphanumeric</option>
            <option value="numeric">Numeric</option>
            <option value="url">URL</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Min Length</label>
          <input
            type="number"
            min={0}
            value={(minRule?.value as number) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRule(
                'string.minLength',
                v ? { kind: 'string.minLength', value: Number(v) } : null,
              );
            }}
            placeholder="No minimum"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Max Length</label>
          <input
            type="number"
            min={0}
            value={(maxRule?.value as number) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRule(
                'string.maxLength',
                v ? { kind: 'string.maxLength', value: Number(v) } : null,
              );
            }}
            placeholder="No maximum"
            style={inputStyle}
          />
        </div>
      </div>
    );
  }
  if (field.type === 'number') {
    const minR = findRule('number.min'),
      maxR = findRule('number.max'),
      intR = findRule('number.int');
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          borderTop: '1px solid var(--border)',
          paddingTop: 'var(--sp-4)',
        }}
      >
        <label style={{ ...labelStyle, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Validation
        </label>
        <div>
          <label style={labelStyle}>Min</label>
          <input
            type="number"
            value={(minR?.value as number) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRule('number.min', v ? { kind: 'number.min', value: Number(v) } : null);
            }}
            placeholder="No minimum"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Max</label>
          <input
            type="number"
            value={(maxR?.value as number) ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setRule('number.max', v ? { kind: 'number.max', value: Number(v) } : null);
            }}
            placeholder="No maximum"
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={labelStyle}>Integer only</label>
          <ToggleSwitch
            active={!!intR}
            onToggle={() =>
              setRule('number.int', intR ? null : { kind: 'number.int', value: true })
            }
          />
        </div>
      </div>
    );
  }
  if (field.type === 'date') {
    const minR = findRule('date.min'),
      maxR = findRule('date.max');
    const preventFuture = (maxR?.value as string) === 'today',
      preventPast = (minR?.value as string) === 'today';
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
          borderTop: '1px solid var(--border)',
          paddingTop: 'var(--sp-4)',
        }}
      >
        <label style={{ ...labelStyle, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Validation
        </label>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={labelStyle}>Prevent future</label>
          <ToggleSwitch
            active={preventFuture}
            onToggle={() =>
              setRule('date.max', preventFuture ? null : { kind: 'date.max', value: 'today' })
            }
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <label style={labelStyle}>Prevent past</label>
          <ToggleSwitch
            active={preventPast}
            onToggle={() =>
              setRule('date.min', preventPast ? null : { kind: 'date.min', value: 'today' })
            }
          />
        </div>
      </div>
    );
  }
  return null;
}

// ─── Step 2: Upload Images ───

function StepUploadImages({
  pages,
  formDefinitionId,
  db,
  orgId,
}: {
  pages: FormTemplatePageRow[];
  formDefinitionId: string;
  db: any;
  orgId: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (!fileList || !db || !orgId) return;
      setUploading(true);
      try {
        for (let i = 0; i < fileList.length; i++) {
          const file = fileList[i];
          const id = crypto.randomUUID();
          // Get dimensions from a temporary blob URL
          const tempUrl = URL.createObjectURL(file);
          const dims = await getImageDimensions(tempUrl);
          URL.revokeObjectURL(tempUrl);
          // Upload to Firebase Storage
          const { downloadUrl } = await uploadFormTemplateImage({
            orgId,
            formDefinitionId,
            pageId: id,
            file,
          });
          const pageNumber = pages.length + i + 1;
          await db.execute(
            `INSERT INTO form_template_pages (id, form_definition_id, page_number, image_url, image_width, image_height, label, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
            [
              id,
              formDefinitionId,
              pageNumber,
              downloadUrl,
              dims.width,
              dims.height,
              `Page ${pageNumber}`,
            ],
          );
        }
      } finally {
        setUploading(false);
      }
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [db, formDefinitionId, pages.length, orgId],
  );

  const handleRemovePage = useCallback(
    async (pageId: string) => {
      if (!db) return;
      await db.execute(`DELETE FROM form_template_mappings WHERE page_id = ?`, [pageId]);
      await db.execute(`DELETE FROM form_template_pages WHERE id = ?`, [pageId]);
    },
    [db],
  );

  const handleReorder = useCallback(
    async (pageId: string, direction: 'up' | 'down') => {
      if (!db) return;
      const idx = pages.findIndex((p) => p.id === pageId);
      const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
      if (swapIdx < 0 || swapIdx >= pages.length) return;
      await db.execute(`UPDATE form_template_pages SET page_number = ? WHERE id = ?`, [
        pages[swapIdx].page_number,
        pageId,
      ]);
      await db.execute(`UPDATE form_template_pages SET page_number = ? WHERE id = ?`, [
        pages[idx].page_number,
        pages[swapIdx].id,
      ]);
    },
    [db, pages],
  );

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      <h2 style={headingStyle}>Upload Template Images</h2>
      <p style={subtitleStyle}>Upload scanned form pages. You can reorder them after uploading.</p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        style={{ ...addButtonStyle, marginBottom: 'var(--sp-4)', opacity: uploading ? 0.6 : 1 }}
      >
        <NavIcon name="plus" /> {uploading ? 'Uploading...' : 'Upload Images'}
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        {pages.map((page, idx) => (
          <div
            key={page.id}
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-3)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-3)',
            }}
          >
            <img
              src={page.image_url}
              alt={page.label ?? `Page ${page.page_number}`}
              style={{
                width: 80,
                height: 100,
                objectFit: 'cover',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--border)',
              }}
            />
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {page.label ?? `Page ${page.page_number}`}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {page.image_width} x {page.image_height}px
              </div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                onClick={() => handleReorder(page.id, 'up')}
                disabled={idx === 0}
                style={smallBtnStyle}
              >
                &uarr;
              </button>
              <button
                onClick={() => handleReorder(page.id, 'down')}
                disabled={idx === pages.length - 1}
                style={smallBtnStyle}
              >
                &darr;
              </button>
              <button onClick={() => handleRemovePage(page.id)} style={smallBtnStyle}>
                <NavIcon name="x" />
              </button>
            </div>
          </div>
        ))}
        {pages.length === 0 && (
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              border: '2px dashed var(--border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            No pages uploaded yet. Click &quot;Upload Images&quot; to add template pages.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 3: Map Fields (Designer) ───

function StepMapFields({
  pages,
  mappings,
  fields,
  formDefinitionId,
  formName,
  db,
}: {
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  fields: FormFieldDef[];
  formDefinitionId: string;
  /** PRD.325 — resolved live so a header following the name tracks renames. */
  formName: string;
  db: any;
}) {
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [dragging, setDragging] = useState<{ startX: number; startY: number } | null>(null);
  const [newBox, setNewBox] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [selectedFieldKey, setSelectedFieldKey] = useState('');
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const currentPage = pages[selectedPageIdx];
  const pageMappings = useMemo(
    () => (currentPage ? mappings.filter((m) => m.page_id === currentPage.id) : []),
    [mappings, currentPage],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!imageContainerRef.current || !selectedFieldKey) return;
      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setDragging({ startX: x, startY: y });
      setNewBox({ x, y, w: 0, h: 0 });
    },
    [selectedFieldKey],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging || !imageContainerRef.current) return;
      const rect = imageContainerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      setNewBox({
        x: Math.min(dragging.startX, x),
        y: Math.min(dragging.startY, y),
        w: Math.abs(x - dragging.startX),
        h: Math.abs(y - dragging.startY),
      });
    },
    [dragging],
  );

  const handleMouseUp = useCallback(async () => {
    if (!dragging || !newBox || !currentPage || !db || !selectedFieldKey) {
      setDragging(null);
      setNewBox(null);
      return;
    }
    if (newBox.w < 1 || newBox.h < 1) {
      setDragging(null);
      setNewBox(null);
      return;
    }
    // PRD.325 — the Form Header is a SPECIAL mapping with no backing field, so
    // it is placed through the same drag flow but skips the field lookup.
    const isHeader = selectedFieldKey === FORM_HEADER_FIELD_KEY;
    const field = fields.find((f) => f.key === selectedFieldKey);
    if (!isHeader && !field) return;
    await db.execute(
      `INSERT INTO form_template_mappings (id, form_definition_id, page_id, field_key, field_type, x_pct, y_pct, w_pct, h_pct, config, sort_order, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
      [
        crypto.randomUUID(),
        formDefinitionId,
        currentPage.id,
        selectedFieldKey,
        isHeader ? FORM_HEADER_FIELD_TYPE : field!.type,
        newBox.x,
        newBox.y,
        newBox.w,
        newBox.h,
        isHeader
          ? writeFormHeaderConfig('{}', FORM_HEADER_DEFAULTS)
          : JSON.stringify(field!.config ?? {}),
        pageMappings.length,
      ],
    );
    setDragging(null);
    setNewBox(null);
    setSelectedFieldKey('');
  }, [
    dragging,
    newBox,
    currentPage,
    db,
    selectedFieldKey,
    fields,
    formDefinitionId,
    pageMappings.length,
  ]);

  const handleDeleteMapping = useCallback(
    async (mappingId: string) => {
      if (db) await db.execute(`DELETE FROM form_template_mappings WHERE id = ?`, [mappingId]);
    },
    [db],
  );

  /**
   * PRD.323 — persist mapping presentation.
   *
   * Writes the whole `config` blob back through `writeMappingPresentation`,
   * which preserves keys this feature does not own (binding/drawing options).
   * Geometry columns are untouched, so saving options can never move or resize
   * a placement.
   */
  /** PRD.325 — save an already-formed config blob (Form Header controls). */
  const handleMappingRawConfigChange = useCallback(
    async (mappingId: string, config: string) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_template_mappings SET config = ?, updated_at = datetime('now') WHERE id = ?`,
        [config, mappingId],
      );
    },
    [db],
  );

  const handleMappingConfigChange = useCallback(
    async (mappingId: string, rawConfig: unknown, patch: Partial<MappingPresentation>) => {
      if (!db) return;
      await db.execute(
        `UPDATE form_template_mappings SET config = ?, updated_at = datetime('now') WHERE id = ?`,
        [writeMappingPresentation(rawConfig, patch), mappingId],
      );
    },
    [db],
  );

  if (pages.length === 0)
    return (
      <div style={{ padding: 'var(--sp-5)', textAlign: 'center', color: 'var(--text-muted)' }}>
        No pages uploaded. Go back to Step 2.
      </div>
    );

  return (
    <div
      style={{ padding: 'var(--sp-5)', display: 'flex', gap: 'var(--sp-4)', flex: 1, minHeight: 0 }}
    >
      <div
        style={{
          width: 240,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div>
          <label style={labelStyle}>Page</label>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {pages.map((p, i) => (
              <button
                key={p.id}
                onClick={() => setSelectedPageIdx(i)}
                style={{
                  ...smallBtnStyle,
                  background: i === selectedPageIdx ? 'var(--color-primary)' : 'var(--bg-surface)',
                  color: i === selectedPageIdx ? '#fff' : 'var(--text)',
                }}
              >
                {p.page_number}
              </button>
            ))}
          </div>
        </div>
        {/* PRD.325 — special elements are template chrome, not patient fields,
            so they sit in their own group rather than polluting the field list
            (and therefore `fields_schema`, bindings and the unmapped warning). */}
        <div>
          <label style={labelStyle}>Special elements</label>
          <button
            onClick={() =>
              setSelectedFieldKey(
                selectedFieldKey === FORM_HEADER_FIELD_KEY ? '' : FORM_HEADER_FIELD_KEY,
              )
            }
            style={{
              ...smallBtnStyle,
              width: '100%',
              textAlign: 'left',
              background:
                selectedFieldKey === FORM_HEADER_FIELD_KEY
                  ? 'var(--color-primary)'
                  : 'var(--bg-surface)',
              color: selectedFieldKey === FORM_HEADER_FIELD_KEY ? '#fff' : 'var(--text)',
            }}
          >
            + Form Header
          </button>
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--text-muted)',
              marginTop: 4,
              lineHeight: 1.5,
            }}
          >
            Optional. Defaults to the form name and follows later renames.
          </div>
        </div>

        <div>
          <label style={labelStyle}>Select field to place</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {fields.map((f) => {
              const mappingCount = mappings.filter((m) => m.field_key === f.key).length;
              return (
                <button
                  key={f.key}
                  onClick={() => setSelectedFieldKey(f.key)}
                  style={{
                    ...smallBtnStyle,
                    width: '100%',
                    justifyContent: 'flex-start',
                    padding: '6px 10px',
                    height: 'auto',
                    background:
                      selectedFieldKey === f.key ? 'var(--color-primary)' : 'var(--bg-surface)',
                    color: selectedFieldKey === f.key ? '#fff' : 'var(--text)',
                    opacity: 1,
                  }}
                >
                  <FieldTypeBadge type={f.type} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{f.label || f.key}</span>
                  {mappingCount > 0 && (
                    <span
                      style={{
                        fontSize: 9,
                        background:
                          selectedFieldKey === f.key
                            ? 'rgba(255,255,255,0.3)'
                            : 'var(--bg-surface-2)',
                        borderRadius: 'var(--radius-xs)',
                        padding: '0 4px',
                        lineHeight: '16px',
                        flexShrink: 0,
                      }}
                    >
                      {mappingCount}x
                    </span>
                  )}
                </button>
              );
            })}
            {fields.length === 0 && (
              <div
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)', padding: 4 }}
              >
                No fields defined. Go to Step 1.
              </div>
            )}
          </div>
        </div>
        <div>
          <label style={labelStyle}>Placed on this page</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {pageMappings.map((m) => {
              const field = fields.find((f) => f.key === m.field_key);
              const headerRow = isFormHeaderMapping(m);
              return (
                <div
                  key={m.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 8px',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--font-size-xs)',
                    gap: 4,
                  }}
                >
                  <span
                    style={{
                      color: 'var(--text)',
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {headerRow ? 'Form Header' : field?.label || m.field_key}
                  </span>
                  {/* PRD.323 — the three cramped 22px alignment buttons that
                      used to sit here are RESURFACED inside Mapping Options,
                      alongside the Form Label controls. Same `config.textAlign`
                      key, so nothing already saved changes meaning. */}
                  <MappingOptionsButton
                    mapping={m}
                    fieldLabel={field?.label}
                    fieldType={m.field_type}
                    onSave={handleMappingConfigChange}
                    onSaveRaw={handleMappingRawConfigChange}
                  />
                  <button
                    onClick={() => handleDeleteMapping(m.id)}
                    style={{
                      ...smallBtnStyle,
                      width: 20,
                      height: 20,
                      padding: 0,
                      border: 'none',
                      flexShrink: 0,
                    }}
                  >
                    <NavIcon name="x" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
        {selectedFieldKey && (
          <div
            style={{
              marginBottom: 'var(--sp-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-primary)',
              fontWeight: 'var(--font-weight-medium)',
            }}
          >
            Draw a rectangle to place &quot;
            {fields.find((f) => f.key === selectedFieldKey)?.label || selectedFieldKey}&quot;
          </div>
        )}
        <div
          ref={imageContainerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          style={{
            position: 'relative',
            display: 'inline-block',
            cursor: selectedFieldKey ? 'crosshair' : 'default',
            userSelect: 'none',
          }}
        >
          <img
            src={currentPage.image_url}
            alt={`Page ${currentPage.page_number}`}
            style={{
              maxWidth: '100%',
              display: 'block',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}
            draggable={false}
          />
          {pageMappings.map((m) => {
            const field = fields.find((f) => f.key === m.field_key);
            // PRD.323 — same resolver the Preview and final Render use, so
            // what is positioned here is what prints.
            const stepPresentation = readMappingPresentation(m.config);
            return (
              <div
                key={m.id}
                style={{
                  position: 'absolute',
                  left: `${m.x_pct}%`,
                  top: `${m.y_pct}%`,
                  width: `${m.w_pct}%`,
                  height: `${m.h_pct}%`,
                  border: `2px solid ${getMappingColor(m.field_type)}`,
                  background: `${getMappingColor(m.field_type)}20`,
                  borderRadius: 2,
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 10,
                  color: getMappingColor(m.field_type),
                  fontWeight: 600,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  padding: '0 2px',
                }}
              >
                {/* PRD.324 — a Rich Text mapping is a FLOW ANCHOR. The builder
                    shows a compact one-row marker, never the full default
                    content: its runtime height is patient-specific, so drawing
                    it here would imply a fixed extent that does not exist.
                    Position, width, drag, resize and delete all behave as
                    before. */}
                {isFormHeaderMapping(m) ? (
                  // PRD.325 — the SAME renderer Preview and Render use, at a
                  // reduced scale for the builder's smaller canvas.
                  <FormHeaderBox
                    config={readFormHeaderConfig(m.config)}
                    formName={formName}
                    scale={0.6}
                  />
                ) : m.field_type === 'rich-text' ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      gap: 4,
                      overflow: 'hidden',
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {field?.label || m.field_key}
                    </span>
                    <span
                      style={{
                        flexShrink: 0,
                        padding: '0 4px',
                        borderRadius: 2,
                        border: '1px solid currentColor',
                        fontSize: 8,
                        letterSpacing: 0.4,
                      }}
                    >
                      FLOW
                    </span>
                  </div>
                ) : (
                  <MappedFieldBox presentation={stepPresentation} fieldLabel={field?.label}>
                    {field?.label || m.field_key}
                  </MappedFieldBox>
                )}
              </div>
            );
          })}
          {newBox && newBox.w > 0 && newBox.h > 0 && (
            <div
              style={{
                position: 'absolute',
                left: `${newBox.x}%`,
                top: `${newBox.y}%`,
                width: `${newBox.w}%`,
                height: `${newBox.h}%`,
                border: '2px dashed var(--color-primary)',
                background: 'rgba(37, 99, 235, 0.1)',
                borderRadius: 2,
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Preview ───

function StepPreview({
  pages,
  mappings,
  fields,
  formName,
}: {
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  fields: FormFieldDef[];
  formName: string;
}) {
  /** Sample values, so Preview shows realistic content in every mapped field. */
  const previewValues = useMemo(() => {
    const map: Record<string, string> = {};
    for (const f of fields) map[f.key] = resolvePreviewValue(f);
    return map;
  }, [fields]);

  const unmappedFields = useMemo(
    () =>
      fields.filter(RENDER_MAPPABLE).filter((f) => !mappings.some((m) => m.field_key === f.key)),
    [fields, mappings],
  );

  return (
    <div style={{ padding: 'var(--sp-5)' }}>
      <h2 style={headingStyle}>Preview</h2>
      <p style={subtitleStyle}>
        Preview how the form looks with sample data rendered in mapped fields.
      </p>
      {pages.length === 0 && (
        <div style={warningBannerStyle}>
          <NavIcon name="alert-triangle" /> No template pages uploaded. Go to Step 2.
        </div>
      )}
      {unmappedFields.length > 0 && (
        <div style={warningBannerStyle}>
          <NavIcon name="alert-triangle" /> {unmappedFields.length} unmapped field
          {unmappedFields.length > 1 ? 's' : ''}:{' '}
          {unmappedFields.map((f) => f.label || f.key).join(', ')}
        </div>
      )}
      {/* PRD.324 — Preview renders through the SAME `TemplateOverlayView` the
          patient sees, fed sample values. Parity is structural rather than
          promised: page assignment, Rich Text pagination, continuation sheets
          and downstream reflow are one implementation, so Preview cannot drift
          from Render. Only the chrome above differs. */}
      <TemplateOverlayView
        pages={pages}
        mappings={mappings}
        fields={fields}
        values={previewValues}
        signatures={{}}
        schema={{ version: 1, sections: [{ id: 'preview', title: '', fields }] }}
        bindingData={SAMPLE_DATA}
        formName={formName}
        onValueChange={() => {}}
        onSignatureSave={() => {}}
        readOnly
      />
    </div>
  );
}

// ─── Step 5: Save Draft ───

function StepSaveDraft({
  form,
  schema,
  pages,
  mappings,
  db,
  onSaved,
}: {
  form: FormDefinitionRow | null;
  schema: FormFieldsSchemaV1;
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  db: any;
  onSaved: () => void;
}) {
  const fields = useMemo(() => flattenFields(schema), [schema]);
  const [name, setName] = useState(form?.name ?? '');
  const [description, setDescription] = useState(form?.description ?? '');
  const [purpose, setPurpose] = useState<FormPurpose>(formPurposeFromStored(form?.purpose));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // PRD.305 follow-up — Laboratory Services a Lab Work form is available for.
  const [serviceSearch, setServiceSearch] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);

  const { data: allServices = [] } = usePowerSyncQuery<{
    id: string;
    name: string | null;
    is_lab_test: number | null;
    active: number | null;
  }>(qAppointmentServices());
  const { data: assignmentRows = [] } = usePowerSyncQuery<{ id: string; service_id: string }>(
    form?.id ? qLabWorkFormAssignments(form.id) : qNoOp('lab-work-assignments'),
  );

  // Hydrate the picker once from what is stored; later edits are the user's.
  useEffect(() => {
    if (assignmentsLoaded || !form?.id) return;
    setSelectedServiceIds(new Set(assignmentRows.map((a) => a.service_id)));
    setAssignmentsLoaded(true);
  }, [assignmentsLoaded, form?.id, assignmentRows]);

  // Only is_lab_test services are eligible — never a category name or id.
  const labServiceOptions = useMemo(
    () => selectableLabFormServices(allServices, serviceSearch),
    [allServices, serviceSearch],
  );

  const isLabWork = purpose === 'lab_work';
  const labServicesMissing = isLabWork && selectedServiceIds.size === 0;

  const handleSave = useCallback(async () => {
    if (!db || !form) return;
    if (labServicesMissing) return;
    setSaving(true);
    try {
      const purposeVal = formPurposeToStored(purpose);
      await db.execute(
        `UPDATE form_definitions SET name = ?, description = ?, purpose = ?, fields_schema = ?, updated_at = datetime('now') WHERE id = ?`,
        [name, description, purposeVal, stringifyFieldsSchemaV1(schema), form.id],
      );

      // Reconcile the many-to-many assignment. Deterministic ids mean a retried
      // save converges rather than duplicating a (service, form) pair.
      const current = new Set(assignmentRows.map((a) => a.service_id));
      const desired = isLabWork ? selectedServiceIds : new Set<string>();
      const now = new Date().toISOString();

      for (const serviceId of desired) {
        if (current.has(serviceId)) continue;
        await db.execute(
          `INSERT INTO laboratory_service_forms
            (id, org_id, service_id, form_definition_id, created_at, updated_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [
            laboratoryServiceFormId(serviceId, form.id),
            form.org_id ?? '',
            serviceId,
            form.id,
            now,
            now,
          ],
        );
      }
      for (const assignment of assignmentRows) {
        if (desired.has(assignment.service_id)) continue;
        await db.execute(`DELETE FROM laboratory_service_forms WHERE id = ?`, [assignment.id]);
      }

      setSaved(true);
      onSaved();
    } finally {
      setSaving(false);
    }
  }, [
    db,
    form,
    name,
    description,
    purpose,
    schema,
    onSaved,
    isLabWork,
    labServicesMissing,
    selectedServiceIds,
    assignmentRows,
  ]);

  return (
    <div style={{ padding: 'var(--sp-5)', maxWidth: 600 }}>
      <h2 style={headingStyle}>Save Draft</h2>
      <p style={subtitleStyle}>Review form details and save as draft.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <div>
          <label style={labelStyle}>Form Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              setSaved(false);
            }}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
          />
        </div>
        <div>
          <label style={labelStyle}>Purpose</label>
          <select
            value={purpose}
            onChange={(e) => {
              setPurpose(e.target.value as FormPurpose);
              setSaved(false);
            }}
            style={inputStyle}
          >
            {FORM_PURPOSES.map((p) => (
              <option key={p} value={p}>
                {FORM_PURPOSE_LABELS[p]}
              </option>
            ))}
          </select>
          {purpose === 'registration' && (
            <p
              style={{
                margin: 'var(--sp-2) 0 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              Registration forms power /patient/register. Only one published registration form is
              active at a time — archive or unpublish others to switch.
            </p>
          )}
          {isLabWork && (
            <p
              style={{
                margin: 'var(--sp-2) 0 0',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--text-muted)',
              }}
            >
              Lab Work forms capture a structured laboratory result. Unlike Registration, a clinic
              can publish many — each is offered on the laboratory services you assign below.
            </p>
          )}
        </div>

        {/* PRD.305 follow-up — Laboratory Services. Required for Lab Work, and
            restricted to services flagged `is_lab_test`; assigning a form never
            changes that flag, and eligibility is never read from a category. */}
        {isLabWork && (
          <div>
            <label style={labelStyle}>
              Laboratory Services<span style={{ color: 'var(--color-danger)' }}> *</span>
            </label>

            {selectedServiceIds.size > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--sp-2)',
                  marginBottom: 'var(--sp-2)',
                }}
              >
                {[...selectedServiceIds].map((id) => {
                  const svc = allServices.find((x) => x.id === id);
                  return (
                    <button
                      key={id}
                      type="button"
                      title="Remove"
                      onClick={() => {
                        setSelectedServiceIds((prev) => {
                          const next = new Set(prev);
                          next.delete(id);
                          return next;
                        });
                        setSaved(false);
                      }}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '2px var(--sp-2)',
                        borderRadius: 'var(--radius-xs)',
                        border: '1px solid var(--border)',
                        background: 'var(--bg-surface-2)',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--text)',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      {svc?.name ?? id} ×
                    </button>
                  );
                })}
              </div>
            )}

            <input
              type="text"
              value={serviceSearch}
              onChange={(e) => setServiceSearch(e.target.value)}
              placeholder="Search laboratory tests…"
              style={inputStyle}
            />

            <div
              style={{
                marginTop: 'var(--sp-2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {labServiceOptions.length === 0 ? (
                <div
                  style={{
                    padding: 'var(--sp-3)',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                  }}
                >
                  No laboratory tests found. Mark a service as a Laboratory Test under Appointments
                  &rarr; Manage Services first.
                </div>
              ) : (
                labServiceOptions.map((svc, i) => {
                  const checked = selectedServiceIds.has(svc.id);
                  return (
                    <button
                      key={svc.id}
                      type="button"
                      role="checkbox"
                      aria-checked={checked}
                      onClick={() => {
                        setSelectedServiceIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(svc.id)) next.delete(svc.id);
                          else next.add(svc.id);
                          return next;
                        });
                        setSaved(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--sp-3)',
                        width: '100%',
                        textAlign: 'left',
                        padding: 'var(--sp-3)',
                        background: checked ? 'var(--bg-surface-2)' : 'transparent',
                        border: 'none',
                        borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                        fontFamily: 'inherit',
                        cursor: 'pointer',
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 'var(--radius-xs)',
                          border: `2px solid ${checked ? 'var(--color-primary)' : 'var(--border)'}`,
                          background: checked ? 'var(--color-primary)' : 'transparent',
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                        {svc.name}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            {labServicesMissing && (
              <p
                style={{
                  margin: 'var(--sp-2) 0 0',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                }}
              >
                Choose at least one laboratory service so this form can be offered at Result Ready.
              </p>
            )}
          </div>
        )}
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-3)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
              marginBottom: 8,
            }}
          >
            Summary
          </div>
          <div style={summaryRow}>Sections: {schema.sections.length}</div>
          <div style={summaryRow}>Fields: {fields.length}</div>
          <div style={summaryRow}>Pages: {pages.length}</div>
          <div style={summaryRow}>Field mappings: {mappings.length}</div>
          <div style={summaryRow}>Status: {form?.status ?? 'draft'}</div>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !name.trim() || labServicesMissing}
          style={{
            height: 40,
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: saved ? 'var(--color-success-bg)' : 'var(--color-primary)',
            color: saved ? 'var(--color-success-text)' : '#fff',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Draft'}
        </button>
      </div>
    </div>
  );
}

// ─── Step 6: Publish ───

function StepPublish({
  form,
  pages,
  fields,
  mappings,
  db,
  navigate,
}: {
  form: FormDefinitionRow | null;
  pages: FormTemplatePageRow[];
  fields: FormFieldDef[];
  mappings: FormTemplateMappingRow[];
  db: any;
  navigate: ReturnType<typeof useNavigate>;
}) {
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(form?.status === 'published');
  const isRenderMode = form?.default_view === 'render';
  const hasPages = pages.length > 0;
  const unmapped = fields
    .filter(RENDER_MAPPABLE)
    .filter((f) => !mappings.some((m) => m.field_key === f.key));

  // Pages + mapping only required for render-mode forms
  const hasRenderIssues = isRenderMode && (!hasPages || unmapped.length > 0);

  // Registration forms must have a two_way patient_number binding
  const isRegistration = form?.purpose === 'registration';
  const hasPatientNumberBinding = fields.some(
    (f) =>
      f.binding?.source === 'patient' &&
      f.binding?.bindingKey === 'patient_number' &&
      f.binding?.mode === 'two_way',
  );
  const missingRegistrationField = isRegistration && !hasPatientNumberBinding;

  const canPublish = fields.length > 0 && !hasRenderIssues && !missingRegistrationField;

  const handlePublish = useCallback(async () => {
    if (!db || !form || !canPublish) return;
    setPublishing(true);
    try {
      await db.execute(
        `UPDATE form_definitions SET status = 'published', updated_at = datetime('now') WHERE id = ?`,
        [form.id],
      );
      setPublished(true);
    } finally {
      setPublishing(false);
    }
  }, [db, form, canPublish]);

  return (
    <div style={{ padding: 'var(--sp-5)', maxWidth: 500 }}>
      <h2 style={headingStyle}>Publish Form</h2>
      <p style={subtitleStyle}>Publishing makes this form available for patient documents.</p>
      {published ? (
        <div
          style={{
            border: '1px solid var(--color-success-text)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
            background: 'var(--color-success-bg)',
            color: 'var(--color-success-text)',
            textAlign: 'center',
            marginBottom: 'var(--sp-4)',
          }}
        >
          <div
            style={{
              fontSize: 'var(--font-size-lg)',
              fontWeight: 'var(--font-weight-semi)',
              marginBottom: 4,
            }}
          >
            Form Published
          </div>
          <div style={{ fontSize: 'var(--font-size-sm)' }}>This form is now available for use.</div>
        </div>
      ) : (
        <>
          {isRenderMode && !hasPages && (
            <div style={warningBannerStyle}>
              No template pages uploaded. Render-mode forms require at least 1 page.
            </div>
          )}
          {isRenderMode && unmapped.length > 0 && (
            <div style={warningBannerStyle}>
              {unmapped.length} unmapped field{unmapped.length > 1 ? 's' : ''}:{' '}
              {unmapped.map((f) => f.label || f.key).join(', ')}
            </div>
          )}
          {fields.length === 0 && (
            <div style={warningBannerStyle}>
              No fields defined. Add at least one field in Step 1.
            </div>
          )}
          {missingRegistrationField && (
            <div style={warningBannerStyle}>
              Registration forms require a field bound to Patient Number with &ldquo;Editable
              binding (writes back)&rdquo; mode. Add one in Step 1.
            </div>
          )}
          {canPublish && (
            <div
              style={{
                border: '1px solid var(--color-warning-text)',
                borderRadius: 'var(--radius-md)',
                padding: 'var(--sp-4)',
                background: 'var(--color-warning-bg)',
                color: 'var(--color-warning-text)',
                marginBottom: 'var(--sp-4)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Once published, this form will be available for use. You can always create a new
              version later.
            </div>
          )}
        </>
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-3)' }}>
        {!published && (
          <button
            onClick={handlePublish}
            disabled={publishing || !canPublish}
            style={{
              flex: 1,
              height: 40,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: !canPublish ? 'var(--text-disabled, #d1d5db)' : 'var(--color-primary)',
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-semi)',
              cursor: publishing || !canPublish ? 'not-allowed' : 'pointer',
              opacity: publishing ? 0.7 : 1,
            }}
          >
            {publishing
              ? 'Publishing...'
              : !canPublish
                ? 'Fix issues above to publish'
                : 'Publish Now'}
          </button>
        )}
        <button
          onClick={() => navigate('/clinic/forms')}
          style={{
            flex: 1,
            height: 40,
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            color: 'var(--text)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: 'pointer',
          }}
        >
          Back to Forms List
        </button>
      </div>
    </div>
  );
}

// ─── Inline Editable Name ───

function InlineFormName({ name, formId, db }: { name: string; formId: string; db: any }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!editing) setValue(name);
  }, [name, editing]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  const save = useCallback(async () => {
    setEditing(false);
    const trimmed = value.trim() || 'Untitled Form';
    if (trimmed !== name && db)
      await db.execute(
        `UPDATE form_definitions SET name = ?, updated_at = datetime('now') WHERE id = ?`,
        [trimmed, formId],
      );
  }, [value, name, db, formId]);

  if (editing)
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') {
            setValue(name);
            setEditing(false);
          }
        }}
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 'var(--font-weight-semi)' as any,
          color: 'var(--text)',
          margin: 0,
          border: 'none',
          borderBottom: '2px solid var(--color-primary)',
          background: 'transparent',
          outline: 'none',
          padding: '0 0 2px',
          width: '100%',
          minWidth: 200,
          fontFamily: 'inherit',
        }}
      />
    );
  return (
    <h1
      onClick={() => setEditing(true)}
      title="Click to rename"
      style={{
        fontSize: 'var(--font-size-xl)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text)',
        margin: 0,
        cursor: 'text',
        borderBottom: '2px solid transparent',
        padding: '0 0 2px',
      }}
    >
      {name || 'Untitled Form'}
    </h1>
  );
}

// ─── Default View Toggle ───

function DefaultViewToggle({
  currentView,
  formId,
  db,
}: {
  currentView: string;
  formId: string;
  db: any;
}) {
  const handleToggle = useCallback(
    async (view: string) => {
      if (!db || !formId || view === currentView) return;
      await db.execute(
        `UPDATE form_definitions SET default_view = ?, updated_at = datetime('now') WHERE id = ?`,
        [view, formId],
      );
    },
    [db, formId, currentView],
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>Default:</span>
      {(['form', 'render'] as const).map((v) => {
        const active = currentView === v;
        return (
          <button
            key={v}
            onClick={() => handleToggle(v)}
            style={{
              height: 20,
              padding: '0 8px',
              fontSize: 11,
              border: '1px solid var(--border)',
              borderRadius:
                v === 'form'
                  ? 'var(--radius-sm) 0 0 var(--radius-sm)'
                  : '0 var(--radius-sm) var(--radius-sm) 0',
              marginLeft: v === 'render' ? -1 : 0,
              background: active ? 'var(--color-primary, #2563eb)' : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text-muted)',
              cursor: 'pointer',
              fontWeight: active ? 600 : 400,
            }}
          >
            {v === 'form' ? 'Form' : 'Render'}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main Wizard ───

export function FormWizardPage() {
  const { formId } = useParams<{ formId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const brandTokens = useBrandTokens();
  const primary = brandTokens['--color-primary'] || '#2563eb';
  const { db } = usePowerSyncDb();
  const { claims } = useAuth();
  const orgId = claims?.activeOrg ?? '';
  const currentStep = Number(searchParams.get('step') || '1');
  const { data: formRows = [] } = usePowerSyncQuery<FormDefinitionRow>(
    qFormDefinitionById(formId ?? ''),
  );
  const form = formRows[0] ?? null;
  const { data: pages = [] } = usePowerSyncQuery<FormTemplatePageRow>(
    qFormTemplatePages(formId ?? ''),
  );
  const { data: mappings = [] } = usePowerSyncQuery<FormTemplateMappingRow>(
    qFormTemplateMappings(formId ?? ''),
  );

  const [localSchema, setLocalSchema] = useState<FormFieldsSchemaV1>({ version: 1, sections: [] });
  useEffect(() => {
    if (form) setLocalSchema(parseFieldsSchemaV1(form.fields_schema));
  }, [form?.id]);

  const allFields = useMemo(() => flattenFields(localSchema), [localSchema]);

  const setStep = useCallback(
    (step: number) => setSearchParams({ step: String(step) }),
    [setSearchParams],
  );

  const handleSchemaChange = useCallback(
    async (schema: FormFieldsSchemaV1) => {
      setLocalSchema(schema);
      if (db && formId)
        await db.execute(
          `UPDATE form_definitions SET fields_schema = ?, updated_at = datetime('now') WHERE id = ?`,
          [stringifyFieldsSchemaV1(schema), formId],
        );
    },
    [db, formId],
  );

  if (!formId)
    return (
      <div style={{ padding: 'var(--sp-5)', color: 'var(--text-muted)' }}>No form selected.</div>
    );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: 'var(--sp-5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <button
            onClick={() => navigate('/clinic/forms')}
            style={{
              width: 32,
              height: 32,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted)',
            }}
          >
            <NavIcon name="chevron-left" />
          </button>
          <div>
            <InlineFormName name={form?.name ?? ''} formId={formId} db={db} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                v{form?.version ?? 1} &middot; {form?.status ?? 'draft'}
              </span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                &middot;
              </span>
              <DefaultViewToggle
                currentView={form?.default_view ?? 'form'}
                formId={formId}
                db={db}
              />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setStep(Math.max(1, currentStep - 1))}
            disabled={currentStep <= 1}
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
              background: 'var(--bg-surface)',
              color: currentStep <= 1 ? 'var(--text-disabled)' : 'var(--text)',
              fontSize: 'var(--font-size-sm)',
              cursor: currentStep <= 1 ? 'not-allowed' : 'pointer',
            }}
          >
            Previous
          </button>
          <button
            onClick={() => setStep(Math.min(6, currentStep + 1))}
            disabled={currentStep >= 6}
            style={{
              height: 32,
              padding: '0 12px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: currentStep >= 6 ? 'var(--text-disabled)' : primary,
              color: '#fff',
              fontSize: 'var(--font-size-sm)',
              cursor: currentStep >= 6 ? 'not-allowed' : 'pointer',
            }}
          >
            Next
          </button>
        </div>
      </div>

      <StepProgress currentStep={currentStep} onStepClick={setStep} primary={primary} />

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: currentStep === 1 ? 'hidden' : 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {currentStep === 1 && (
          <StepDefineFields schema={localSchema} onChange={handleSchemaChange} />
        )}
        {currentStep === 2 && (
          <StepUploadImages pages={pages} formDefinitionId={formId} db={db} orgId={orgId} />
        )}
        {currentStep === 3 && (
          <StepMapFields
            pages={pages}
            mappings={mappings}
            fields={allFields}
            formDefinitionId={formId}
            formName={form?.name ?? ''}
            db={db}
          />
        )}
        {currentStep === 4 && (
          <StepPreview
            pages={pages}
            mappings={mappings}
            fields={allFields}
            formName={form?.name ?? ''}
          />
        )}
        {currentStep === 5 && (
          <StepSaveDraft
            form={form}
            schema={localSchema}
            pages={pages}
            mappings={mappings}
            db={db}
            onSaved={() => {}}
          />
        )}
        {currentStep === 6 && (
          <StepPublish
            form={form}
            pages={pages}
            fields={allFields}
            mappings={mappings}
            db={db}
            navigate={navigate}
          />
        )}
      </div>
    </div>
  );
}

// ─── Shared styles & helpers ───

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)' as any,
  color: 'var(--text-muted)',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};
const inputStyle: CSSProperties = {
  width: '100%',
  height: 36,
  padding: '0 10px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  outline: 'none',
  boxSizing: 'border-box',
};
const headingStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-semi)' as any,
  color: 'var(--text)',
  margin: '0 0 var(--sp-2)',
};
const subtitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-muted)',
  margin: '0 0 var(--sp-4)',
};
const addButtonStyle: CSSProperties = {
  height: 38,
  padding: '0 16px',
  borderRadius: 'var(--radius-md)',
  border: '1px dashed var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)' as any,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: 6,
};
const smallBtnStyle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  flexShrink: 0,
};
const iconBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  color: 'var(--text-muted)',
  borderRadius: 'var(--radius-sm)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const badgeStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-medium)' as any,
  lineHeight: '18px',
  padding: '0 6px',
  borderRadius: 'var(--radius-xs)',
  background: 'var(--bg-surface-2, #f3f4f6)',
  color: 'var(--text-muted)',
};
const summaryRow: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text)',
  padding: '2px 0',
};
const warningBannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: 'var(--sp-3)',
  marginBottom: 'var(--sp-3)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-warning-bg, #fefce8)',
  border: '1px solid var(--color-warning-text, #a16207)',
  color: 'var(--color-warning-text, #a16207)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)' as any,
};

function getMappingColor(fieldType: string): string {
  switch (fieldType) {
    case 'binding':
      return '#2563eb';
    case 'drawing':
      return '#16a34a';
    case 'signature':
      return '#dc2626';
    case 'text':
    case 'textarea':
    case 'number':
      return '#d97706';
    case 'date':
      return '#0891b2';
    case 'select':
    case 'multi-select':
      return '#7c3aed';
    case 'yes-no':
      return '#6d28d9';
    default:
      return '#6b7280';
  }
}

function FieldTypeBadge({ type }: { type: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: getMappingColor(type),
        marginRight: 6,
        flexShrink: 0,
      }}
    />
  );
}

function getImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 800, height: 1100 });
    img.src = url;
  });
}

function resolvePreviewValue(field: FormFieldDef | undefined): string {
  if (!field) return '—';
  if (field.type === 'drawing') return field.label || 'Drawing Area';
  // V2 binding metadata
  if (field.binding) {
    const key = `${field.binding.source}.${field.binding.bindingKey}`;
    return SAMPLE_DATA[key] ?? `[${key}]`;
  }
  // Legacy binding type
  if (field.type === 'binding' && field.config) {
    const cfg = field.config as BindingConfig;
    return SAMPLE_DATA[`${cfg.source}.${cfg.path}`] ?? `[${cfg.source}.${cfg.path}]`;
  }
  if (field.type === 'yes-no') return 'Yes';
  if (field.type === 'signature') return field.label || 'Signature';
  if (field.type === 'date') return '2026-03-20';
  if (field.type === 'number') return '42';
  if (field.type === 'select' || field.type === 'multi-select') return 'Option A';
  if (field.type === 'textarea') return 'Sample long text...';
  return field.label || field.key;
}

// ─── PRD.323 — Mapping Options ───

/**
 * `…` popover on each placed mapping.
 *
 * Scope is deliberately fixed at Form Label + value Alignment. This is not a
 * typography panel: no fonts, sizes or colours, because every one of those
 * would need a matching interpretation in three renderers.
 */
function MappingOptionsButton({
  mapping,
  fieldLabel,
  fieldType,
  onSave,
  onSaveRaw,
}: {
  mapping: FormTemplateMappingRow;
  fieldLabel: string | undefined;
  fieldType: string;
  onSave: (
    mappingId: string,
    rawConfig: unknown,
    patch: Partial<MappingPresentation>,
  ) => Promise<void>;
  /** Writes an already-serialized config — used by the Form Header controls. */
  onSaveRaw: (mappingId: string, config: string) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const presentation = readMappingPresentation(mapping.config);

  const isHeader = isFormHeaderMapping({ field_type: fieldType, field_key: mapping.field_key });
  const headerConfig = readFormHeaderConfig(mapping.config);
  const canAlign = supportsMappingAlignment(fieldType);
  const canLabel = supportsMappingLabel(fieldType);

  const patchHeader = (p: Partial<FormHeaderConfig>) =>
    void onSaveRaw(mapping.id, writeFormHeaderConfig(mapping.config, p));

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const patch = (p: Partial<MappingPresentation>) => void onSave(mapping.id, mapping.config, p);

  // Nothing configurable for this field type — offering an empty menu would be
  // worse than offering none.
  if (!isHeader && !canAlign && !canLabel) return null;

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        title="Mapping options"
        aria-label="Mapping options"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...smallBtnStyle,
          width: 22,
          height: 20,
          padding: 0,
          border: '1px solid var(--border)',
        }}
      >
        …
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            width: 230,
            padding: 'var(--sp-3)',
            background: 'var(--bg-surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md, 0 4px 16px rgba(0,0,0,0.12))',
            zIndex: 30,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-3)',
          }}
        >
          {/* PRD.325 — a header has no Form Label and no value alignment; it has
              its own source, colour and size. Same popover surface as PRD.323. */}
          {isHeader && (
            <>
              <div style={sectionHeadingStyle}>Form Header</div>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <label style={labelStyle}>Use Form Name</label>
                <ToggleSwitch
                  active={headerConfig.useFormName}
                  onToggle={() => patchHeader({ useFormName: !headerConfig.useFormName })}
                />
              </div>
              <div>
                <label style={labelStyle}>Header Text</label>
                <input
                  type="text"
                  value={headerConfig.headerText}
                  // Editable even while following the form name, so typed text
                  // is retained and toggling back does not lose it.
                  onChange={(e) => patchHeader({ headerText: e.target.value })}
                  placeholder={headerConfig.useFormName ? 'Using form name' : 'Custom header'}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Alignment</label>
                <SegmentedChoice
                  options={MAPPING_ALIGNMENTS}
                  value={headerConfig.alignment}
                  onChange={(alignment) => patchHeader({ alignment })}
                />
              </div>
              <div>
                <label style={labelStyle}>Font Color</label>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {FORM_HEADER_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      title={FORM_HEADER_COLOR_LABELS[c]}
                      aria-label={FORM_HEADER_COLOR_LABELS[c]}
                      onClick={() => patchHeader({ color: c })}
                      style={{
                        flex: 1,
                        minWidth: 32,
                        height: 24,
                        borderRadius: 'var(--radius-sm)',
                        border: `2px solid ${
                          headerConfig.color === c ? 'var(--color-primary)' : 'var(--border)'
                        }`,
                        background: FORM_HEADER_COLOR_VALUES[c],
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </div>
                {/* No contrast guarantee is claimed: the template is an
                    arbitrary uploaded image and analysing it is out of scope. */}
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--text-muted)',
                    marginTop: 4,
                  }}
                >
                  Choose a color with good contrast against the template.
                </div>
              </div>
              <div>
                <label style={labelStyle}>Font Size</label>
                <SegmentedChoice
                  options={FORM_HEADER_SIZES}
                  value={headerConfig.size}
                  onChange={(size) => patchHeader({ size })}
                />
              </div>
            </>
          )}

          {!isHeader && canLabel && (
            <>
              <div style={sectionHeadingStyle}>Form Label</div>
              <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <label style={labelStyle}>Show label</label>
                <ToggleSwitch
                  active={presentation.showLabel}
                  onToggle={() =>
                    patch({
                      showLabel: !presentation.showLabel,
                      // Seed from the canonical field label the FIRST time only.
                      // After that the stored text is authoritative, so a later
                      // field-label edit cannot rewrite the printed form.
                      labelText:
                        !presentation.showLabel && presentation.labelText === ''
                          ? resolveMappingLabelText(presentation, fieldLabel)
                          : presentation.labelText,
                    })
                  }
                />
              </div>

              {presentation.showLabel && (
                <>
                  <div>
                    <label style={labelStyle}>Label</label>
                    <input
                      type="text"
                      value={presentation.labelText}
                      onChange={(e) => patch({ labelText: e.target.value })}
                      placeholder={fieldLabel ?? ''}
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Position</label>
                    <SegmentedChoice
                      options={MAPPING_LABEL_POSITIONS}
                      value={presentation.labelPosition}
                      onChange={(labelPosition) => patch({ labelPosition })}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {!isHeader && canAlign && (
            <>
              <div style={sectionHeadingStyle}>Value</div>
              <div>
                <label style={labelStyle}>Alignment</label>
                <SegmentedChoice
                  options={MAPPING_ALIGNMENTS}
                  value={presentation.alignment}
                  onChange={(alignment) => patch({ alignment })}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const sectionHeadingStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
};

/** Small segmented control, generic so each caller keeps its literal union. */
function SegmentedChoice<T extends string>({
  options,
  value,
  onChange,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            style={{
              flex: 1,
              padding: '4px 0',
              borderRadius: 'var(--radius-sm)',
              border: `1px solid ${active ? 'var(--color-primary)' : 'var(--border)'}`,
              background: active ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              textTransform: 'capitalize',
              cursor: 'pointer',
            }}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}
